import { skyMourningBrideContact } from './121-sky-mourning-bride.mjs';
import { skyCinderheartSlagContact } from './119-sky-cinderheart-slag.mjs';
import { skyAuthoredEnemyContact } from './118-sky-authored-enemies.mjs';
import { skyAuthoredBossContact } from './116-sky-authored-boss.mjs';
import { SKY_FLIGHT_AIM } from './86-sky-world-scale.mjs';
import { skyEffectiveEquipment } from './82-sky-bonuses.mjs';
import { skyEnemyWeakpoint } from './79-sky-precision.mjs';
import { skyWeaponMuzzle } from './90-sky-weapon-muzzles.mjs';
import { skyLeviathanTriangleSweep, skyLeviathanMeshSweep } from './96-sky-leviathan-contacts.mjs';
import {
  skyFlightFrame,
  skyFlightPointToLocal,
  skyFlightPointToWorld,
  skyFlightVectorToWorld,
} from './92-sky-flight-frame.mjs';

/** Collision aperture radius in metres. Canonical targets are required;
 * legacy enemies without an authored aperture retain the 1.25m default. */
export function skyTargetRadius(target) {
  if (
    target.contactShape === 'box' &&
    target.halfExtents &&
    ['x', 'y', 'z'].every(
      (axis) => Number.isFinite(target.halfExtents[axis]) && target.halfExtents[axis] > 0,
    )
  )
    return Math.hypot(target.halfExtents.x, target.halfExtents.y, target.halfExtents.z);
  return target.contactShape === 'sphere' && Number.isFinite(target.radius) && target.radius > 0
    ? target.radius
    : target.kind === 'boss'
      ? (target.body?.radius ?? target.weakpoint?.radius ?? 1.25)
      : target.kind === 'pillar'
        ? (target.radius ?? 1.6)
        : (target.radius ?? 1.25);
}
const centre = (target) => (target.kind === 'boss' ? (target.weakpoint ?? target) : target);
/** Nearest coarse body plane in metres; anatomical apertures own their own z. */
export const skyTargetFront = (target) => target.body?.z ?? target.z + skyTargetRadius(target);
const front = (target) =>
  target.kind === 'boss' ? (target.weakpoint?.z ?? skyTargetFront(target)) : skyTargetFront(target);

/** A supplied valid manual ray selects measured manual-fire origins in every
 * active phase. Legacy diagnostic callers that omit a sight retain their
 * existing pilot-axis solution; accepting a ray never grants weapon permission. */
export function skyDirectAimActive(state) {
  return (
    state.status === 'playing' &&
    Boolean(skyValidatedAimPoint(state, state.aimPoint) || skyDirectAimView(state))
  );
}

/** Pause retains the same observer view and sight position; only simulation
 * and accepted actions require playing. Terminal states restore ordinary view. */
export function skyDirectAimView(state) {
  return (
    ['playing', 'paused'].includes(state.status) &&
    !state.traversal &&
    state.boss?.controlMode === 'aim'
  );
}

/** Untrusted camera-ray target in world metres. Bound it in the physical pilot
 * frame to130m forward and120m across; the explicit96m encounter plane allows
 *320m across for wide first-person viewport corners. Reject nonfinite/behind values.
 * The point is never snapped to an enemy or moved to a weakspot. */
export function skyValidatedAimPoint(state, point) {
  if (!point || !['x', 'y', 'z'].every((axis) => Number.isFinite(point[axis]))) return null;
  const origin = skyWeaponMuzzle(state, null, 0, 1);
  const basis = skyFlightFrame(state.flightFrame);
  const local = basis
    ? skyFlightPointToLocal({ ...basis, origin }, point)
    : { x: point.x - origin.x, y: point.y - origin.y, z: point.z - origin.z };
  const across =
    basis && state.flightAimDepth === SKY_FLIGHT_AIM.depth
      ? SKY_FLIGHT_AIM.framedAcross
      : SKY_FLIGHT_AIM.normalAcross;
  return local.z < -0.05 &&
    local.z >= -SKY_FLIGHT_AIM.maxDepth &&
    Math.hypot(local.x, local.y) <= across
    ? { x: point.x, y: point.y, z: point.z }
    : null;
}

function directAimSolution(state, equipment, origin, point) {
  const delta = { x: point.x - origin.x, y: point.y - origin.y, z: point.z - origin.z };
  const length = Math.hypot(delta.x, delta.y, delta.z);
  const to = Object.fromEntries(
    ['x', 'y', 'z'].map((axis) => [axis, origin[axis] + (delta[axis] / length) * 130]),
  );
  const contacts = [
    ...(state.enemies ?? []),
    ...(state.boss?.authoredProps ?? []),
    ...(state.boss ? [state.boss] : []),
  ]
    .filter((target) => target.hp > 0 && !target.defeated && target.collisionEnabled !== false)
    .map((target) => ({
      target,
      hit: skyProjectileContact(target, origin, to, target.contactFrame ?? state.flightFrame),
    }))
    .filter((value) => value.hit)
    .sort(
      (a, b) =>
        Math.hypot(a.hit.x - origin.x, a.hit.y - origin.y, a.hit.z - origin.z) -
        Math.hypot(b.hit.x - origin.x, b.hit.y - origin.y, b.hit.z - origin.z),
    );
  const first = contacts[0];
  const basis = skyFlightFrame(state.flightFrame);
  const depth = basis ? -skyFlightPointToLocal({ ...basis, origin }, point).z : origin.z - point.z;
  const flight = depth / equipment.shotSpeed;
  return {
    ...(first?.hit ?? point),
    vx: delta.x / flight,
    vy: delta.y / flight,
    vz: delta.z / flight,
    targetId: first?.target.id ?? null,
    confidence: 0,
    manual: true,
  };
}
const projection = (from, to, z) => {
  const distance = from.z - to.z;
  if (!(distance > 0)) return null;
  const along = (from.z - z) / distance;
  return { x: from.x + (to.x - from.x) * along, y: from.y + (to.y - from.y) * along, z };
};
/** Resolve an exact swept surface contact. Open anatomical cavities cut a hole
 * through the coarse body plane; closed physical covers intercept that same ray.
 * No raycast or presentation transform participates in domain collision. */
function legacyProjectileContact(target, from, to) {
  if (target.kind === 'boss' && target.weakpoints) {
    const contacts = [];
    let throughOpening = false;
    for (const point of target.weakpoints) {
      const hit = projection(from, to, point.z);
      if (!hit || Math.hypot(hit.x - point.x, hit.y - point.y) > point.radius) continue;
      if (point.exposed) throughOpening = true;
      if (point.z <= from.z && point.z >= to.z)
        contacts.push({ ...hit, region: 'core', weakpointId: point.id });
    }
    const body = target.body;
    if (body && !throughOpening && body.z >= to.z && from.z >= target.z - body.radius) {
      const hit = projection(from, to, Math.min(from.z, body.z));
      if (hit && Math.hypot(hit.x - body.x, hit.y - body.y) <= body.radius)
        contacts.push({ ...hit, region: 'armor' });
    }
    return contacts.sort((a, b) => b.z - a.z)[0] ?? null;
  }
  const radius = skyTargetRadius(target),
    point = centre(target);
  if (target.z > from.z + radius || target.z < to.z - radius) return null;
  const hit = projection(from, to, Math.min(from.z, target.z + radius));
  return hit && Math.hypot(hit.x - point.x, hit.y - point.y) <= radius ? hit : null;
}

function localTarget(target, frame) {
  const position = skyFlightPointToLocal(frame, target);
  if (!position) return null;
  const point = (value) => {
    const transformed = skyFlightPointToLocal(frame, value);
    return transformed ? { ...value, ...transformed } : null;
  };
  return {
    ...target,
    ...position,
    contactFrame: undefined,
    body: point(target.body),
    weakpoint: point(target.weakpoint),
    weakpoints: target.weakpoints?.map(point).filter(Boolean),
  };
}

// A rigid mesh already owns local triangles. Transform only the ray through
// the validated basis; cloning target weakpoints and revalidating that basis
// cannot change its surface and multiplied whole-scene aim cost at120Hz.
function framedMeshContact(target, from, to, basis) {
  if (!basis || ![target, from, to].every((p) => p && [p.x, p.y, p.z].every(Number.isFinite)))
    return null;
  const local = (p) => {
    const x = p.x - basis.origin.x,
      y = p.y - basis.origin.y,
      z = p.z - basis.origin.z;
    return {
      x: x * basis.right.x + y * basis.right.y + z * basis.right.z,
      y: x * basis.up.x + y * basis.up.y + z * basis.up.z,
      z: x * basis.back.x + y * basis.back.y + z * basis.back.z,
    };
  };
  const hit = skyLeviathanMeshSweep(local(from), local(to), target.triangles);
  if (!hit) return null;
  const { x, y, z } = hit.point;
  return {
    x: basis.origin.x + (basis.right.x * x + basis.up.x * y + basis.back.x * z),
    y: basis.origin.y + (basis.right.y * x + basis.up.y * y + basis.back.y * z),
    z: basis.origin.z + (basis.right.z * x + basis.up.z * y + basis.back.z * z),
    fraction: hit.fraction,
    region: 'armor',
  };
}

/** Swept world contact in an explicit frame, or target.contactFrame. Framed
 * surfaces are finite disks perpendicular to local Z, with actual open holes;
 * either travel direction works and the earliest segment fraction wins. An
 * invalid supplied frame/segment returns null. Without a frame the established
 * world-Z contact policy is unchanged. Returned point is always world metres. */
export function skyProjectileContact(target, from, to, frame = target.contactFrame) {
  if (target.collisionEnabled === false) return null;
  if (target.authoredEnemyRig) return skyAuthoredEnemyContact(target, from, to);
  if (target.cinderheartSlag) return skyCinderheartSlagContact(target, from, to);
  if (target.mourning) return skyMourningBrideContact(target, from, to);
  if (target.authoredRig) return skyAuthoredBossContact(target, from, to);
  if (frame === undefined || frame === null) return legacyProjectileContact(target, from, to);
  const basis = skyFlightFrame(frame);
  if (target.contactShape === 'mesh') return framedMeshContact(target, from, to, basis);
  const a = skyFlightPointToLocal(basis, from),
    b = skyFlightPointToLocal(basis, to);
  const local = basis && localTarget(target, basis);
  if (!a || !b || !local) return null;
  if (local.contactShape === 'triangle' || local.contactShape === 'triangles') {
    const triangles = local.contactShape === 'triangle' ? [local.triangle] : local.triangles;
    if (!Array.isArray(triangles) || triangles.length > 512) return null;
    let hit = null;
    for (const triangle of triangles) {
      const candidate = skyLeviathanTriangleSweep(a, b, triangle);
      if (candidate && (!hit || candidate.fraction < hit.fraction)) hit = candidate;
    }
    return hit
      ? {
          ...skyFlightPointToWorld(basis, hit.point),
          fraction: hit.fraction,
          region: 'armor',
        }
      : null;
  }
  if (local.contactShape === 'box') {
    const extents = local.halfExtents;
    if (
      !extents ||
      !['x', 'y', 'z'].every((axis) => Number.isFinite(extents[axis]) && extents[axis] > 0)
    )
      return null;
    let enter = 0,
      exit = 1,
      moved = false;
    for (const axis of ['x', 'y', 'z']) {
      const delta = b[axis] - a[axis],
        offset = a[axis] - local[axis];
      if (Math.abs(delta) < 1e-12) {
        if (Math.abs(offset) > extents[axis]) return null;
        continue;
      }
      moved = true;
      const first = (-extents[axis] - offset) / delta,
        last = (extents[axis] - offset) / delta;
      enter = Math.max(enter, Math.min(first, last));
      exit = Math.min(exit, Math.max(first, last));
      if (enter > exit + 1e-9) return null;
    }
    if (!moved) return null;
    const fraction = Math.max(0, Math.min(1, enter));
    return {
      ...skyFlightPointToWorld(basis, {
        x: a.x + (b.x - a.x) * fraction,
        y: a.y + (b.y - a.y) * fraction,
        z: a.z + (b.z - a.z) * fraction,
      }),
      fraction,
      region: 'armor',
    };
  }
  if (local.contactShape === 'sphere') {
    const radius = local.radius;
    if (!Number.isFinite(radius) || radius <= 0) return null;
    const delta = { x: b.x - a.x, y: b.y - a.y, z: b.z - a.z };
    const offset = { x: a.x - local.x, y: a.y - local.y, z: a.z - local.z };
    const speed2 = delta.x ** 2 + delta.y ** 2 + delta.z ** 2;
    if (!(speed2 > 1e-20)) return null;
    const along = offset.x * delta.x + offset.y * delta.y + offset.z * delta.z;
    const outside = offset.x ** 2 + offset.y ** 2 + offset.z ** 2 - radius ** 2;
    const discriminant = along ** 2 - speed2 * outside;
    if (discriminant < 0) return null;
    const fraction = outside <= 0 ? 0 : (-along - Math.sqrt(discriminant)) / speed2;
    if (fraction < -1e-9 || fraction > 1 + 1e-9) return null;
    const t = Math.max(0, Math.min(1, fraction));
    return {
      ...skyFlightPointToWorld(basis, {
        x: a.x + delta.x * t,
        y: a.y + delta.y * t,
        z: a.z + delta.z * t,
      }),
      fraction: t,
    };
  }
  if (Math.abs(b.z - a.z) < 1e-10) return null;
  const atPlane = (point) => {
    const fraction = (point.z - a.z) / (b.z - a.z);
    const hit = {
      x: a.x + (b.x - a.x) * fraction,
      y: a.y + (b.y - a.y) * fraction,
      z: point.z,
      fraction,
    };
    return Number.isFinite(fraction) && Math.hypot(hit.x - point.x, hit.y - point.y) <= point.radius
      ? hit
      : null;
  };
  const within = (hit) => hit && hit.fraction >= -1e-9 && hit.fraction <= 1 + 1e-9;
  const contacts = [];
  if (local.kind === 'boss' && local.weakpoints) {
    const holes = [];
    for (const point of local.weakpoints) {
      const hit = atPlane(point);
      if (hit && point.exposed) holes.push(hit);
      if (within(hit))
        contacts.push({ ...hit, region: point.exposed ? 'core' : 'armor', weakpointId: point.id });
    }
    const body = local.body && atPlane(local.body);
    // A recessed opening also admits an outward ray already inside its cavity;
    // its line intersection can lie behind the current segment in that case.
    if (within(body) && !holes.length) contacts.push({ ...body, region: 'armor' });
  } else {
    const point = centre(local),
      radius = skyTargetRadius(local);
    const hit = atPlane({ ...point, z: local.z + radius, radius });
    if (within(hit)) contacts.push(hit);
  }
  const hit = contacts.sort((x, y) => x.fraction - y.fraction)[0];
  return hit
    ? {
        ...hit,
        fraction: Math.max(0, Math.min(1, hit.fraction)),
        ...skyFlightPointToWorld(basis, hit),
      }
    : null;
}

/** Deterministic, non-homing fire solution in metres and metres/second. Assist
 * captures only nearby screen-aligned targets and leaves a precision gap:
 * ordinary craft retain42cm; boss gaps follow their physical critical aperture.
 * the player must still align the gold cockpit for critical damage. Nearer
 * collision apertures block acquisition, including pillars and closed armor.
 * The renderer consumes the same endpoint; emitted bolts retain this velocity.
 * Capture uses forward clearance from the canonical origin, so close physical
 * head passes remain aimable without acquiring a target behind the barrel.
 * A valid state.flightFrame resolves world targets through a pilot-centred
 * local basis and returns world endpoint/velocity; invalid frames explicitly
 * retain legacy world-Z behavior. The optional origin is always world metres. */
export function skyAimSolution(state, equipment = skyEffectiveEquipment(state), origin) {
  if (skyValidatedAimPoint(state, state.aimPoint) || skyDirectAimView(state)) {
    const source = origin ?? skyWeaponMuzzle(state, null, 0, 1);
    const basis = skyFlightFrame(state.flightFrame);
    const direct =
      skyValidatedAimPoint(state, state.aimPoint) ??
      (basis
        ? skyFlightPointToWorld({ ...basis, origin: source }, { x: 0, y: 0, z: -34 })
        : { x: source.x, y: source.y, z: source.z - 34 });
    return directAimSolution(state, equipment, source, direct);
  }
  const frame = skyFlightFrame(state.flightFrame);
  if (!frame) return localAimSolution(state, equipment, origin ?? { x: state.x, y: state.y, z: 0 });
  // Centre the working frame at the canonical fallback muzzle. This retains
  // existing130m ray/34m empty-space sight policy at any centreline world depth.
  const centre = skyWeaponMuzzle(state, null, 0, 1);
  const working = { ...frame, origin: { x: centre.x, y: centre.y, z: centre.z } };
  const source = skyFlightPointToLocal(working, origin ?? centre) ?? { x: 0, y: 0, z: 0 };
  const originals = new Map();
  const project = (target) => {
    if (!target) return null;
    const local = localTarget(target, working);
    if (local) originals.set(local, target);
    return local;
  };
  const boss = project(state.boss);
  // Keep the same target ownership in local space. A shallow projected boss
  // otherwise retains world-space props which bypass the original-target map.
  if (boss) boss.authoredProps = (state.boss.authoredProps ?? []).map(project).filter(Boolean);
  const localState = {
    ...state,
    flightFrame: null,
    enemies: (state.enemies ?? []).map(project).filter(Boolean),
    boss,
  };
  const contact = (target, from, to) => {
    const original = originals.get(target);
    const hit = skyProjectileContact(
      original,
      skyFlightPointToWorld(working, from),
      skyFlightPointToWorld(working, to),
      original.contactFrame ?? working,
    );
    return hit ? { ...hit, ...skyFlightPointToLocal(working, hit) } : null;
  };
  const cockpit = (target) => {
    const original = originals.get(target);
    return skyFlightFrame(original.contactFrame)
      ? {
          ...skyEnemyWeakpoint(original),
          ...skyFlightPointToLocal(working, skyEnemyWeakpoint(original)),
        }
      : skyEnemyWeakpoint(target);
  };
  const obstructed = (target, to) =>
    [...originals.keys()].some(
      (other) => other !== target && other.hp > 0 && !other.defeated && contact(other, source, to),
    );
  const solution = localAimSolution(localState, equipment, source, contact, cockpit, obstructed);
  const velocity = skyFlightVectorToWorld(working, {
    x: solution.vx,
    y: solution.vy,
    z: solution.vz,
  });
  return {
    ...solution,
    ...skyFlightPointToWorld(working, solution),
    vx: velocity.x,
    vy: velocity.y,
    vz: velocity.z,
  };
}

function localAimSolution(
  state,
  equipment,
  origin,
  contactAt = skyProjectileContact,
  cockpitAt = skyEnemyWeakpoint,
  obstructed,
) {
  const { x, y, z: originZ } = origin;
  const targets = [
    ...(state.enemies ?? []),
    ...(state.boss?.authoredProps ?? []),
    ...(state.boss ? [state.boss] : []),
  ]
    .filter(
      (target) =>
        target.hp > 0 &&
        target.collisionEnabled !== false &&
        !target.defeated &&
        front(target) < originZ - 0.05 &&
        front(target) >= originZ - 120,
    )
    .sort((a, b) => front(b) - front(a) || a.id - b.id);
  const firstContact = (to) =>
    targets
      .map((target) => ({ target, hit: contactAt(target, origin, to) }))
      .filter((value) => value.hit)
      .sort((a, b) => b.hit.z - a.hit.z || a.target.id - b.target.id)[0];
  const straight = firstContact({ x, y, z: -130 });
  const fallback = {
    x,
    y,
    z: straight?.hit.z ?? -34,
    vx: 0,
    vy: 0,
    vz: -equipment.shotSpeed,
    targetId: null,
    confidence: 0,
  };
  for (const target of targets) {
    if (target.kind === 'pillar' || (target.kind === 'boss' && !target.vulnerable)) continue;
    const point = target.kind === 'boss' ? centre(target) : cockpitAt(target);
    const dx = point.x - x,
      dy = point.y - y,
      distance = Math.hypot(dx, dy);
    if (distance > equipment.aimCapture) continue;
    const precisionGap =
      target.kind === 'boss' && point.criticalRadius
        ? point.criticalRadius + Math.min(0.08, (point.radius - point.criticalRadius) * 0.35)
        : 0.42;
    const correction = Math.min(
      distance * equipment.aimStrength,
      Math.max(0, distance - precisionGap),
    );
    const fraction = distance > 0 ? correction / distance : 0;
    const endX = x + dx * fraction,
      endY = y + dy * fraction,
      z = front(target);
    const blocked = obstructed
      ? obstructed(target, { x: endX, y: endY, z })
      : targets.some((other) => {
          if (other === target || skyTargetFront(other) <= z) return false;
          const along = (skyTargetFront(other) - originZ) / (z - originZ),
            point = other.body ?? centre(other);
          return (
            Math.hypot(x + (endX - x) * along - point.x, y + (endY - y) * along - point.y) <=
            skyTargetRadius(other)
          );
        });
    if (blocked) continue;
    const flight = (originZ - z) / equipment.shotSpeed;
    const vx = (endX - x) / flight,
      vy = (endY - y) / flight;
    const rayTime = (originZ + 130) / equipment.shotSpeed;
    const contact = firstContact({ x: x + vx * rayTime, y: y + vy * rayTime, z: -130 });
    return {
      x: contact?.hit.x ?? endX,
      y: contact?.hit.y ?? endY,
      z: contact?.hit.z ?? z,
      vx,
      vy,
      vz: -equipment.shotSpeed,
      targetId: contact?.target.id ?? target.id,
      confidence: equipment.aimStrength * (1 - distance / equipment.aimCapture),
    };
  }
  return fallback;
}
